import React, { useEffect, useState } from 'react';
import { metricsService } from '../MetricsService';
import { TenantMetricsData, TenantMetricsProps } from '../Model/MetricsModel';
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Tooltip,
  Fab,
  Icon,
} from '@mui/material';
import { Dictionary } from '../../../Common/CommonModel';
import { convertValueToString } from '../Helpers/MetricsHelpers';
import { capitalize, getLocalDate } from '../../../../Helpers/UtilityMethods';
import { useFabStyles, useDashboardStyles } from '../../../../Common/Styles';
import OverallUserMetricsChart from './Charts/OverallUserMetricsChart';
import { callService } from '../../../../Service/ServiceBase';
import { RequestType } from '../../../../Service/RequestType';

import { Loader } from '../../../../Common/Components/Loader';
import { showToast } from '../../../../lib/toast';
import { Tenant } from '../../ManageFarms/Model/FarmModel';
import { tenantService } from '../../Tenant/TenantService';
import { CustomerV2 } from '../../Customer/Model/CustomerModel';
import { customerService } from '../../Customer/CustomerService';

enum MetricType {
  Storage = 'Storage',
  User = 'User',
  Time = 'Time',
  OnBoard = 'Onboard',
  Updated = 'Updated',
  Version = 'Version',
}

const generateTypeColor = (type: MetricType) => {
  switch (type) {
    case MetricType.Storage:
      return '#6d4d7e';
    case MetricType.User:
      return '#342c39';
    case MetricType.Time:
      return '#2c3e50';
    case MetricType.OnBoard:
      return '#2c3e50';
    case MetricType.Updated:
      return '#2c3e50';
    case MetricType.Version:
      return '#2c3e60';
  }
};

export default function TenantMetrics(props: Readonly<TenantMetricsProps>) {
  const { classes } = useDashboardStyles();
  const { classes: fabStyles } = useFabStyles();

  const { farmId, tenantId } = props;

  const [latestTenantMetrics, setlatestTenantMetrics] = useState<
    TenantMetricsData[]
  >([]);
  const [customerMetricsNameMap, setCustomerMetricsNameMap] = useState<
    Dictionary<TenantMetricsData>
  >({});

  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState<Tenant>();
  const [customer, setCustomer] = useState<CustomerV2>();

  useEffect(() => {
    loadMetrics();
    loadTenant();
    loadCustomer();
  }, [tenantId]);

  const loadMetrics = async () => {
    setLoading(true);
    const metrics = await metricsService.getLatestTenantMetrics(tenantId);
    const metricsNameMapping: Dictionary<TenantMetricsData> = {};
    metrics.forEach((metric) => {
      metricsNameMapping[metric.Name] = metric;
    });
    setlatestTenantMetrics(metrics);
    setCustomerMetricsNameMap(metricsNameMapping);
    setLoading(false);
  };

  const loadTenant = async () => {
    setLoading(true);
    const tenant = await tenantService.getTenant(tenantId);
    setTenant(tenant);
  };

  const loadCustomer = async () => {
    setLoading(true);
    const customer = await customerService.getCustomer(tenantId);
    setCustomer(customer);
  };

  const refreshTenantMetrics = async () => {
    setLoading(true);
    callService('metrics', `tenant/${tenantId}`, RequestType.POST).then(() => {
      showToast({
        title: 'metrics updated successfully!',
        type: 'success',
      });
      loadMetrics();
    });
  };

  const getSelectedMetricsData = (metricName: string) => {
    return customerMetricsNameMap[metricName];
  };

  return (
    <div className={classes.root}>
      {loading ? (
        <Loader loading={loading} />
      ) : (
        <div>
          <div className={classes.displayRoot}>
            <Tooltip title={'refresh metrics'}>
              <Fab
                className={fabStyles.firstFab}
                color="primary"
                onClick={refreshTenantMetrics}
              >
                <Icon>sync</Icon>
              </Fab>
            </Tooltip>
            <Card
              className={classes.papper}
              sx={{
                backgroundColor: generateTypeColor(MetricType.Updated),
              }}
            >
              <CardContent>
                <Typography className={classes.pos} color="#fff" variant="h6">
                  Last Synced
                </Typography>

                <Typography variant="h4" component="h2" color="#fff">
                  {latestTenantMetrics[0] &&
                    getLocalDate(latestTenantMetrics[0].TimeStamp)}
                </Typography>
              </CardContent>
            </Card>
            <Card
              className={classes.papper}
              sx={{
                backgroundColor: generateTypeColor(MetricType.OnBoard),
              }}
            >
              <CardContent>
                <Typography className={classes.pos} color="#fff" variant="h6">
                  Onboard Date
                </Typography>

                <Typography variant="h4" component="h2" color="#fff">
                  {tenant && getLocalDate(tenant.OnboardedDate)}
                </Typography>
              </CardContent>
            </Card>
            <Card
              className={classes.papper}
              sx={{
                backgroundColor: generateTypeColor(MetricType.Time),
              }}
            >
              <CardContent>
                <Typography className={classes.pos} color="#fff" variant="h6">
                  Last Deployed
                </Typography>

                <Typography variant="h4" component="h2" color="#fff">
                  {customer && getLocalDate(customer.DeploymentDate)}
                </Typography>
              </CardContent>
            </Card>
            {latestTenantMetrics.map((metrics) => (
              <Card
                className={classes.papper}
                key={metrics.Id}
                sx={{
                  backgroundColor: generateTypeColor(
                    capitalize(metrics.Type) as MetricType,
                  ),
                }}
              >
                <CardContent>
                  <Typography className={classes.pos} color="#fff" variant="h6">
                    {capitalize(getSelectedMetricsData(metrics.Name).Type)}
                  </Typography>
                  <Typography
                    className={classes.title}
                    color="#fff"
                    gutterBottom
                  >
                    {getSelectedMetricsData(metrics.Name).DisplayName}:
                  </Typography>
                  <Typography variant="h4" component="h2" color="#fff">
                    {convertValueToString(getSelectedMetricsData(metrics.Name))}
                  </Typography>
                </CardContent>
              </Card>
            ))}
            <Card
              className={classes.papper}
              sx={{
                backgroundColor: generateTypeColor(MetricType.Version),
              }}
            >
              <CardContent>
                <Typography className={classes.pos} color="#fff" variant="h6">
                  Version
                </Typography>

                <Typography variant="h4" component="h2" color="#fff">
                  {tenant?.ProVersion}
                </Typography>
              </CardContent>
            </Card>
          </div>
          <Divider />
          <OverallUserMetricsChart
            farmId={farmId}
            tenantId={tenantId}
          ></OverallUserMetricsChart>
        </div>
      )}
    </div>
  );
}
